/**
 * IMPORTANT - BLOCKCHAIN DATA INTEGRITY:
 * This file handles blockchain data which must maintain exact values and case sensitivity.
 * - NEVER use toLowerCase() or toUpperCase()
 * - NEVER normalize strings or addresses
 * - NEVER transform collection names
 * - NEVER modify contract addresses
 * - ALWAYS use exact matches for comparisons
 * - ALWAYS preserve original data as it exists on-chain
 */

const API_ENDPOINTS = {
    COLLECTIONS: 'https://theta-sdk-api.nftfi.com/data/v0/pipes/loans_due_by_collection_endpoint.json',
    LOANS: 'https://theta-sdk-api.nftfi.com/data/v0/pipes/loans_due_endpoint.json'
};

const API_CONFIG = {
    PAGE_SIZE: 1000000
};

export async function fetchCollections(selectedDays = 365) {
    try {
        // Use proxy endpoint for CORS
        const url = `/api/nftfi/data/v0/pipes/loans_due_by_collection_endpoint.json?howDaysFromNow=${selectedDays}&page_size=10000`;
        console.log('[Debug] Fetching collections URL:', url);
        
        const response = await fetch(url);
        console.log('[Debug] Collections response status:', response.status);
        
        const data = await response.json();
        console.log('[Debug] Collections response data structure:', Object.keys(data));
        
        // Ensure we have valid data
        if (!data || !data.data || !Array.isArray(data.data)) {
            console.error('[Error] Invalid collections data format:', data);
            return [];
        }
        
        // Filter out invalid collection entries
        const validCollections = data.data.filter(collection => 
            collection && 
            typeof collection === 'object' && 
            collection.nftProjectName
        );
        
        console.log('[Debug] Valid collections found:', validCollections.length);
        return validCollections;
    } catch (error) {
        console.error('Error fetching collections:', error);
        return [];
    }
}

export async function fetchLoans(nftProjectName, selectedDays = 365, onProgress) {
    let allLoans = [];
    
    console.log('[Debug] Starting to fetch loans for', nftProjectName, 'for', selectedDays, 'days');

    try {
        // Use proxy endpoint for CORS
        const url = `/api/nftfi/data/v0/pipes/loans_due_endpoint.json?daysFromNow=${selectedDays}&page_size=${API_CONFIG.PAGE_SIZE}&page=0&nftProjectName=${encodeURIComponent(nftProjectName)}`;
        console.log('[Debug] Fetching loans URL:', url);
        
        const response = await fetch(url);
        const data = await response.json();

        console.log('[Debug] Response status:', response.status);
        
        if (!data?.data) {
            console.error('[Debug] Invalid response format:', data);
            return [];
        }

        console.log('[Debug] Received', data.data.length, 'loans');
        console.log('[Debug] Sample loan data structure:', data.data[0] ? {
            nftName: data.data[0].nftName,
            nftTokenId: data.data[0].nftTokenId,
            tokenId: data.data[0].tokenId,
            nftId: data.data[0].nftId,
            collateralTokenId: data.data[0].collateralTokenId,
            nftCollateralTokenId: data.data[0].nftCollateralTokenId,
            principalAmountUSD: data.data[0].principalAmountUSD
        } : 'No loans found');
        
        allLoans = data.data;
        
        if (onProgress) {
            onProgress(allLoans.length);
        }
    } catch (error) {
        console.error('Error fetching loan data:', error);
    }

    console.log('[Debug] Total loans fetched:', allLoans.length);
    return allLoans;
}

// Helper function to get date X days ago in YYYY-MM-DD format
function getDateXDaysAgo(days) {
    const date = new Date();
    date.setDate(date.getDate() - days);
    return date.toISOString().split('T')[0];
} 